import { Link } from "@tanstack/react-router";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { HOMEPAGE_QUESTIONS } from "@/data/homepage-questions";

/**
 * "Questions you might be asking yourself…" — a compact, swipeable row of
 * question cards. Each card is real text, and each button opens the public
 * article that answers it. Deliberately horizontal so the homepage does not
 * grow taller on a phone.
 */
export function QuestionsCarousel() {
  return (
    <section
      aria-labelledby="homepage-questions-heading"
      className="mt-2 min-w-0 w-full"
    >
      <div className="mx-auto w-full min-w-0 max-w-5xl lg:max-w-[1180px] text-center">
        <h2
          id="homepage-questions-heading"
          className="font-display text-base leading-tight tracking-tight text-foreground sm:text-xl lg:text-2xl"
        >
          Questions <span className="text-[var(--gold-deep)] underline decoration-gold/70 decoration-4 underline-offset-4">you</span>{" "}
          might be asking yourself…
        </h2>
        <p className="mx-auto mt-1 max-w-xl text-[10px] leading-snug text-foreground/70 sm:text-xs">
          You're not alone. These are some of the questions people most often ask about
          trauma. Explore our articles to find clear, compassionate answers.
        </p>
      </div>

      <div className="mx-auto mt-2 w-full min-w-0 max-w-5xl lg:max-w-[1180px]">
        <Carousel opts={{ align: "start", loop: true, containScroll: "trimSnaps" }}>
          <CarouselContent className="-ms-2 sm:-ms-4">
            {HOMEPAGE_QUESTIONS.map((q) => (
              <CarouselItem
                key={q.id}
                className="ps-2 basis-[68%] sm:ps-4 sm:basis-1/2 lg:basis-1/4"
              >
                <article
                  className={`flex h-full flex-col rounded-2xl border border-gold/25 p-2 shadow-sm sm:p-3 rt-dim-card ${q.tint}`}
                >
                  {/* Speech bubble — the question itself, as text. */}
                  <div className="relative rounded-xl bg-cream/85 px-2.5 py-2 text-center shadow-[0_1px_0_rgba(0,0,0,0.03)]">
                    <h3 className="font-display text-xs font-semibold leading-snug text-[var(--question-ink)] sm:text-sm">
                      {q.question}
                    </h3>
                    <span
                      aria-hidden
                      className="absolute -bottom-1.5 start-6 h-3 w-3 rotate-45 rounded-[3px] bg-cream/85"
                    />
                  </div>

                  <div className="mt-2 flex flex-1 items-center justify-center">
                    <img
                      src={q.image}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      width={816}
                      height={816}
                      className="h-20 w-20 rounded-xl object-cover sm:h-28 sm:w-28"
                    />
                  </div>

                  <p className="mt-1.5 text-center text-[9px] italic leading-snug text-[var(--question-ink)]/70 dark:text-[var(--question-ink)] sm:text-[11px]">
                    {q.note}
                  </p>

                  <Link
                    to="/blog/$slug"
                    params={{ slug: q.slug }}
                    className="mt-2 inline-flex min-h-9 w-full items-center justify-center gap-1.5 rounded-full bg-gradient-to-b from-gold to-[var(--gold-deep)] px-3 text-[11px] font-semibold text-background shadow-sm transition hover:brightness-105 sm:text-xs"
                  >
                    Find out more
                    <span aria-hidden className="inline-block rtl:rotate-180">
                      →
                    </span>
                    <span className="sr-only">about: {q.question}</span>
                  </Link>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="flex h-[44px] w-[44px] -start-1.5 border-gold/40 bg-cream/85 text-[var(--gold-deep)] hover:bg-cream sm:-start-2" />
          <CarouselNext className="flex h-[44px] w-[44px] -end-1.5 border-gold/40 bg-cream/85 text-[var(--gold-deep)] hover:bg-cream sm:-end-2" />
        </Carousel>
      </div>
    </section>
  );
}

export default QuestionsCarousel;
